import { Box, Typography } from '@mui/material';
import { Button } from '@efexteam/components';
import { useTranslation } from 'react-i18next';
import Autorenew from '@mui/icons-material/Autorenew';
import { useFormContext } from 'react-hook-form';
import { UploadStaStakeHolders } from '@efex-services/client/types';
import { useContext } from 'react';
import StakeHolderContext from '@pages/onboarding/Mexico/steps/StakeHolders/context/StakeHolderContext';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LifeProofStyles from './lifeProof.styles';
import useLifeProof from './hooks/useLifeProof';
import ObtainLink from './components/ObtainLink';
import WebFrame from './components/WebFrame';
import { LifeProofProps } from '../lifeProof.types';
import { KycStatuses } from './lifeProof.constants';

const LifeProof = ({
  getStakeHolder,
  handleStakeholderRequest,
  kycStatus,
}: LifeProofProps) => {
  const { t } = useTranslation();
  const { getAllCurrentDocumentsIds } = useContext(StakeHolderContext);

  const {
    watch,
    handleSubmit,
    formState: { defaultValues },
  } = useFormContext();

  const {
    isOpenWebFrame,
    isOpenObtainLink,
    loadingGetUserVerificationId,
    userVerificationId,
    sessionId,
    handleCloseWebFrame,
    handleOpenWebFrame,
    handleCloseObtainLink,
    handleGetUserVerificationId,
  } = useLifeProof({
    defaultValues: defaultValues as UploadStaStakeHolders,
    getStakeHolder,
    handleStakeholderRequest,
    getAllCurrentDocumentsIds,
  });

  const name = watch('name');

  if (kycStatus?.status === KycStatuses.APPROVED) {
    return (
      <Box className={LifeProofStyles.wrapper}>
        <Typography variant="h6">{t('onbLifeProof')}</Typography>

        <Box className={LifeProofStyles.statusApprovedContainer}>
          <CheckCircleOutlineIcon fontSize="small" />

          <Typography variant="body2">
            {t('onbLifeProofStatusApproved')}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={LifeProofStyles.wrapper}>
      <Typography variant="h6">{t('onbLifeProof')}</Typography>

      {kycStatus?.status === KycStatuses.DECLINED && (
        <Box className={LifeProofStyles.statusDeclinedContainer}>
          <ErrorOutlineIcon fontSize="small" />
          <Typography variant="body2">
            {t('onbLifeProofStatusDeclined')}
          </Typography>
        </Box>
      )}

      <Box className={LifeProofStyles.descriptionContainer}>
        <Typography variant="body1">
          {t('onbLifeProofDescriptionOne')}
        </Typography>
        <Typography variant="body1">
          {t('onbLifeProofDescriptionTwo', {
            name,
          })}
        </Typography>
      </Box>

      <Box className={LifeProofStyles.buttonContainer}>
        <Button
          onClick={handleSubmit(async (data) =>
            handleGetUserVerificationId(data as UploadStaStakeHolders)
          )}
          appearance="primary"
          disabled={loadingGetUserVerificationId}
          endIcon={
            loadingGetUserVerificationId ? (
              <Autorenew className="animate-spin" />
            ) : undefined
          }
        >
          {t('onbLifeProofObtainLinkButton')}
        </Button>

        <Button
          onClick={handleSubmit((data) =>
            handleOpenWebFrame(data as UploadStaStakeHolders)
          )}
          appearance="secondary"
          disabled={loadingGetUserVerificationId}
        >
          {t('onbLifeProofVerifyButton')}
        </Button>
      </Box>

      <ObtainLink
        isOpen={isOpenObtainLink}
        onClose={handleCloseObtainLink}
        link={userVerificationId}
      />

      <WebFrame
        isOpen={isOpenWebFrame}
        onClose={handleCloseWebFrame}
        sessionId={sessionId}
      />
    </Box>
  );
};

export default LifeProof;
